import React, { useState } from 'react';
import {
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Switch,
  Tag,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  FileTextOutlined,
  KeyOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { callCenterSettingsApi, show422Error, ValidationError } from '../../utils/api';

export const AI_SETTINGS_FALLBACK_DEFAULTS = {
  gapgpt_api_url: 'https://api.gapgpt.app/v1',
  gapgpt_model: 'gapgpt-qwen-3.5',
  ai_temperature: 0.3,
  ai_max_tokens: 300,
  ai_request_timeout: 60,
  ai_max_transcript_chars: 20000,
  ai_min_transcript_chars: 40,
  ai_rate_limit_per_minute: 30,
};

/** در صورت در دسترس نبودن ai_defaults از سرور */
const FALLBACK_JSON_FIELDS = [
  { key: 'summary', label: 'خلاصه مکالمه' },
  { key: 'customer_request', label: 'درخواست یا مشکل مشتری' },
  { key: 'outcome', label: 'نتیجه تماس' },
  { key: 'sentiment', label: 'احساس مشتری' },
  { key: 'follow_up_needed', label: 'نیاز به پیگیری' },
  { key: 'topics', label: 'موضوعات' },
];
const FALLBACK_JSON_DEFAULT_FIELDS = ['summary', 'customer_request', 'outcome', 'sentiment', 'follow_up_needed'];

const PROMPT_MAX_LENGTH = 20000;

const MODEL_OPTIONS = [
  { value: 'gapgpt-qwen-3.5', label: 'gapgpt-qwen-3.5 — Qwen 3.5 با میزبانی در ایران (پیشنهادی)' },
];

/** مقادیر اولیه فرم از پاسخ GET تنظیمات */
export const mapAiSettingsToForm = (data = {}) => ({
  ai_summary_enabled: Boolean(data.ai_summary_enabled),
  ai_auto_summary: data.ai_auto_summary ?? true,
  gapgpt_api_key: '',
  clear_gapgpt_api_key: false,
  gapgpt_api_url: data.gapgpt_api_url || AI_SETTINGS_FALLBACK_DEFAULTS.gapgpt_api_url,
  gapgpt_model: data.gapgpt_model || AI_SETTINGS_FALLBACK_DEFAULTS.gapgpt_model,
  ai_system_prompt: data.ai_system_prompt || '',
  ai_user_prompt: data.ai_user_prompt || '',
  ai_output_format: ['json', 'custom'].includes(data.ai_output_format) ? data.ai_output_format : 'text',
  ai_summary_json_path: data.ai_summary_json_path || '',
  ai_json_fields: Array.isArray(data.ai_json_fields) && data.ai_json_fields.length
    ? data.ai_json_fields
    : data.ai_defaults?.json_default_fields || FALLBACK_JSON_DEFAULT_FIELDS,
  ai_temperature: data.ai_temperature ?? AI_SETTINGS_FALLBACK_DEFAULTS.ai_temperature,
  ai_top_p: data.ai_top_p ?? null,
  ai_max_tokens: data.ai_max_tokens ?? AI_SETTINGS_FALLBACK_DEFAULTS.ai_max_tokens,
  ai_request_timeout: data.ai_request_timeout ?? AI_SETTINGS_FALLBACK_DEFAULTS.ai_request_timeout,
  ai_max_transcript_chars: data.ai_max_transcript_chars ?? AI_SETTINGS_FALLBACK_DEFAULTS.ai_max_transcript_chars,
  ai_min_transcript_chars: data.ai_min_transcript_chars ?? AI_SETTINGS_FALLBACK_DEFAULTS.ai_min_transcript_chars,
  ai_include_timestamps: Boolean(data.ai_include_timestamps),
  ai_rate_limit_per_minute: data.ai_rate_limit_per_minute ?? AI_SETTINGS_FALLBACK_DEFAULTS.ai_rate_limit_per_minute,
});

/** بخش هوش مصنوعی payload ذخیره تنظیمات */
export const buildAiSettingsPayload = (values) => {
  const payload = {
    ai_summary_enabled: Boolean(values.ai_summary_enabled),
    ai_auto_summary: Boolean(values.ai_auto_summary),
    gapgpt_api_url: values.gapgpt_api_url?.trim() || AI_SETTINGS_FALLBACK_DEFAULTS.gapgpt_api_url,
    gapgpt_model: values.gapgpt_model?.trim() || AI_SETTINGS_FALLBACK_DEFAULTS.gapgpt_model,
    ai_system_prompt: values.ai_system_prompt?.trim() || null,
    ai_user_prompt: values.ai_user_prompt?.trim() || null,
  };

  if (values.ai_output_format) {
    payload.ai_output_format = values.ai_output_format;
  }
  if (values.ai_summary_json_path !== undefined) {
    payload.ai_summary_json_path = values.ai_summary_json_path?.trim() || null;
  }
  if (Array.isArray(values.ai_json_fields)) {
    payload.ai_json_fields = Array.from(new Set(['summary', ...values.ai_json_fields]));
  }

  const numericFields = [
    'ai_temperature',
    'ai_max_tokens',
    'ai_request_timeout',
    'ai_max_transcript_chars',
    'ai_min_transcript_chars',
    'ai_rate_limit_per_minute',
  ];
  numericFields.forEach((field) => {
    if (values[field] !== undefined && values[field] !== null && values[field] !== '') {
      const num = Number(values[field]);
      if (!Number.isNaN(num)) {
        payload[field] = num;
      }
    }
  });

  if (values.ai_top_p !== undefined && values.ai_top_p !== null && values.ai_top_p !== '') {
    const topP = Number(values.ai_top_p);
    if (!Number.isNaN(topP)) {
      payload.ai_top_p = topP;
    }
  }

  if (values.ai_include_timestamps !== undefined) {
    payload.ai_include_timestamps = Boolean(values.ai_include_timestamps);
  }

  if (values.clear_gapgpt_api_key) {
    payload.clear_gapgpt_api_key = true;
  } else if (values.gapgpt_api_key?.trim()) {
    payload.gapgpt_api_key = values.gapgpt_api_key.trim();
  }

  return payload;
};

const SectionCard = ({ icon, title, children, extra }) => (
  <Card
    size="small"
    className="custom-font shadow-sm mb-4"
    title={(
      <span className="custom-font font-bold flex items-center gap-2">
        {icon}
        {title}
      </span>
    )}
    extra={extra}
  >
    {children}
  </Card>
);

const ToggleRow = ({ name, title, description }) => (
  <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-100 dark:border-gray-700/60 h-full">
    <div>
      <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-1 custom-font">{title}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 custom-font">{description}</div>
    </div>
    <Form.Item name={name} valuePropName="checked" className="m-0 shrink-0">
      <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
    </Form.Item>
  </div>
);

/**
 * تب تنظیمات «هوش مصنوعی و خلاصه‌سازی» مرکز تماس (داخل فرم اصلی تنظیمات رندر می‌شود).
 */
const AiSummarySettingsTab = ({ form, settingsMeta }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const defaults = settingsMeta?.ai_defaults || {};
  const promptVariables = defaults.prompt_variables || ['{transcript}', '{direction}', '{duration}', '{agent}', '{talk_stats}'];
  const keyIsSet = Boolean(settingsMeta?.gapgpt_api_key_set);
  const clearKey = Form.useWatch('clear_gapgpt_api_key', form);
  const outputFormat = Form.useWatch('ai_output_format', form);
  // خالی بودن پرامپت کاربر یعنی پیش‌فرض سیستم، که در حالت‌های JSON متفاوت است
  const userPromptDefault = (outputFormat === 'text'
    ? defaults.ai_user_prompt
    : defaults.ai_structured_user_prompt || defaults.ai_user_prompt) || '';
  const jsonFieldOptions = (defaults.json_fields || FALLBACK_JSON_FIELDS).map((field) => ({
    value: field.key,
    label: <span className="custom-font">{field.label}</span>,
    disabled: field.key === 'summary',
  }));

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { gapgpt_api_key: apiKey, gapgpt_api_url: apiUrl, gapgpt_model: model } = form.getFieldsValue([
        'gapgpt_api_key',
        'gapgpt_api_url',
        'gapgpt_model',
      ]);
      const payload = {};
      if (apiKey?.trim()) payload.gapgpt_api_key = apiKey.trim();
      if (apiUrl?.trim()) payload.gapgpt_api_url = apiUrl.trim();
      if (model?.trim()) payload.gapgpt_model = model.trim();

      const res = await callCenterSettingsApi.testGapGpt(payload);
      setTestResult(res?.data ?? {});
      message.success(res?.message || 'ارتباط با GapGPT برقرار شد.');
    } catch (error) {
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'ارتباط با GapGPT برقرار نشد.');
    } finally {
      setTesting(false);
    }
  };

  const appendVariable = (variable) => {
    const current = form.getFieldValue('ai_user_prompt') || userPromptDefault;
    form.setFieldsValue({ ai_user_prompt: `${current}${current.endsWith(' ') || !current ? '' : ' '}${variable}` });
  };

  return (
    <div className="py-2">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4 p-3 bg-purple-50/60 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
        <div>
          <span className="custom-font font-medium text-purple-900 dark:text-purple-200 block">
            تحلیل و خلاصه‌سازی مکالمات رونویسی‌شده با GapGPT
          </span>
          <p className="custom-font text-xs text-purple-700/80 dark:text-purple-300/80 m-0 mt-1">
            متن مکالمات پس از رونویسی موفق، در پس‌زمینه برای مدل ارسال و خلاصه آن روی تماس ذخیره می‌شود.
          </p>
        </div>
        <Space wrap>
          {settingsMeta && (
            <Tag
              icon={keyIsSet ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              color={keyIsSet ? 'success' : 'warning'}
              className="custom-font"
            >
              {keyIsSet ? 'کلید GapGPT ثبت شده' : 'کلید GapGPT ثبت نشده'}
            </Tag>
          )}
          <Button
            icon={<ThunderboltOutlined />}
            loading={testing}
            onClick={handleTest}
            className="custom-font text-purple-600 border-purple-400 hover:text-purple-700 hover:border-purple-500"
          >
            تست ارتباط
          </Button>
        </Space>
      </div>

      {testResult && (
        <Card
          size="small"
          className="mb-4 border-purple-300 dark:border-purple-700 bg-purple-50/40 dark:bg-purple-950/20"
          title={(
            <Space>
              <CheckCircleOutlined className="text-purple-600" />
              <span className="custom-font font-medium">نتیجه تست ارتباط</span>
            </Space>
          )}
          extra={<Button size="small" type="text" onClick={() => setTestResult(null)} className="custom-font">بستن</Button>}
        >
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div>
              <span className="text-gray-500 text-xs block">مدل:</span>
              <Tag color="purple" className="font-mono mt-0.5" dir="ltr">{testResult.model}</Tag>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">زمان پاسخ:</span>
              <Tag color="cyan" className="font-mono mt-0.5" dir="ltr">{testResult.latency_ms} ms</Tag>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">پاسخ مدل:</span>
              <span className="custom-font text-sm font-medium">{testResult.reply || '—'}</span>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <ToggleRow
          name="ai_summary_enabled"
          title="فعال‌سازی خلاصه‌سازی هوش مصنوعی"
          description="امکان خلاصه‌سازی دستی، گروهی و خودکار مکالمات رونویسی‌شده."
        />
        <ToggleRow
          name="ai_auto_summary"
          title="خلاصه‌سازی خودکار پس از رونویسی"
          description="به محض موفقیت رونویسی، خلاصه‌سازی به طور خودکار در صف قرار می‌گیرد."
        />
      </div>

      <SectionCard icon={<KeyOutlined className="text-purple-600" />} title="اتصال به GapGPT">
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              label="کلید دسترسی (API Key)"
              name="gapgpt_api_key"
              extra={keyIsSet ? 'کلید فعلی ذخیره شده است؛ برای تغییر، کلید جدید وارد کنید.' : 'کلید دریافتی از پنل توسعه‌دهندگان GapGPT.'}
            >
              <Input.Password
                placeholder={keyIsSet ? '••••••••••••' : 'sk-...'}
                dir="ltr"
                autoComplete="new-password"
                disabled={clearKey}
                prefix={<KeyOutlined className="text-gray-400" />}
              />
            </Form.Item>
            {keyIsSet && (
              <Form.Item name="clear_gapgpt_api_key" valuePropName="checked" className="-mt-3">
                <Checkbox className="custom-font text-xs">حذف کلید ذخیره‌شده</Checkbox>
              </Form.Item>
            )}
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="آدرس API (Base URL)"
              name="gapgpt_api_url"
              rules={[{ type: 'url', message: 'آدرس معتبر وارد کنید' }]}
              extra={`پیش‌فرض: ${defaults.gapgpt_api_url || AI_SETTINGS_FALLBACK_DEFAULTS.gapgpt_api_url}`}
            >
              <Input placeholder={AI_SETTINGS_FALLBACK_DEFAULTS.gapgpt_api_url} dir="ltr" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="مدل"
              name="gapgpt_model"
              rules={[{ required: true, message: 'نام مدل الزامی است' }]}
              extra="از لیست انتخاب کنید یا شناسه هر مدل دیگری از GapGPT را وارد نمایید."
            >
              <AutoComplete options={MODEL_OPTIONS} placeholder="gapgpt-qwen-3.5" dir="ltr" />
            </Form.Item>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard icon={<CodeOutlined className="text-purple-600" />} title="قالب خروجی">
        <Form.Item name="ai_output_format" className="mb-3">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            className="custom-font"
            options={[
              { value: 'text', label: 'متن ساده (فقط خلاصه)' },
              { value: 'json', label: 'JSON با فیلدهای آماده' },
              { value: 'custom', label: 'JSON سفارشی (ساختار در پرامپت)' },
            ]}
          />
        </Form.Item>

        {outputFormat === 'custom' && (
          <>
            <Form.Item
              label="مسیر کلید خلاصه در JSON (اختیاری)"
              name="ai_summary_json_path"
              rules={[{ pattern: /^[\p{L}\p{N}_]+(\.[\p{L}\p{N}_]+)*$/u, message: 'فقط حروف، اعداد و _ با جداکننده نقطه؛ مثال: call_info.summary' }]}
              extra="مقدار این کلید به عنوان خلاصه تماس نمایش داده می‌شود. خالی = اولین کلید summary در هر سطحی از JSON."
            >
              <Input placeholder="call_info.summary" dir="ltr" />
            </Form.Item>
            <p className="custom-font text-xs text-gray-500 dark:text-gray-400 m-0 leading-6">
              ساختار کامل خروجی (کلیدها، انواع و مقادیر مجاز) را در «دستورالعمل سیستمی» تعریف کنید؛ سیستم چیزی به آن اضافه نمی‌کند
              و کل JSON دریافتی روی تماس ذخیره می‌شود. زمان‌بندی گفتار همیشه همراه متن ارسال می‌شود و آمار دقیق زمان صحبت
              اپراتور/مشتری از طریق متغیر <span dir="ltr" className="font-mono">{'{talk_stats}'}</span> در اختیار مدل قرار می‌گیرد.
              سقف توکن خروجی حداقل ۴۰۹۶ و مهلت پاسخ حداقل ۱۸۰ ثانیه در نظر گرفته می‌شود.
            </p>
          </>
        )}

        {outputFormat === 'json' && (
          <>
            <Form.Item
              label="فیلدهای تحلیل"
              name="ai_json_fields"
              extra="«خلاصه مکالمه» همیشه استخراج می‌شود و در ستون خلاصه تماس نمایش داده می‌شود. هر فیلد اضافه، توکن و هزینه بیشتری مصرف می‌کند."
            >
              <Checkbox.Group options={jsonFieldOptions} className="custom-font flex flex-wrap gap-y-2" />
            </Form.Item>
            <p className="custom-font text-xs text-gray-500 dark:text-gray-400 m-0">
              ابتدا با حالت JSON رسمی (response_format) درخواست داده می‌شود؛ اگر مدل آن را پشتیبانی نکند، به صورت خودکار
              فقط با دستورالعمل پرامپت ادامه می‌دهد. اگر پاسخ مدل JSON معتبر نباشد، خلاصه به صورت متن ساده ذخیره می‌شود.
              سقف توکن خروجی در این حالت حداقل ۶۰۰ در نظر گرفته می‌شود.
            </p>
          </>
        )}

        {outputFormat === 'text' && (
          <p className="custom-font text-xs text-gray-500 dark:text-gray-400 m-0">
            خروجی مدل یک خلاصه متنی ۱ تا ۲ خطی است که دقیقاً طبق پرامپت‌های زیر تولید می‌شود.
          </p>
        )}
      </SectionCard>

      <SectionCard icon={<FileTextOutlined className="text-purple-600" />} title="پرامپت‌ها">
        <Form.Item
          label={(
            <div className="flex items-center gap-2">
              <span>دستورالعمل سیستمی (System Prompt)</span>
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => form.setFieldsValue({ ai_system_prompt: defaults.ai_system_prompt || '' })}
                className="custom-font text-xs p-0 h-auto"
              >
                پیش‌فرض
              </Button>
            </div>
          )}
          name="ai_system_prompt"
          extra="نقش مدل، زبان، طول و سبک خلاصه را مشخص می‌کند. خالی = متن پیش‌فرض سیستم."
          rules={[{ max: PROMPT_MAX_LENGTH, message: 'حداکثر ۲۰٬۰۰۰ کاراکتر' }]}
        >
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 18 }} placeholder={defaults.ai_system_prompt} showCount maxLength={PROMPT_MAX_LENGTH} />
        </Form.Item>

        <Form.Item
          label={(
            <div className="flex items-center gap-2">
              <span>قالب پیام کاربر (User Prompt)</span>
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => form.setFieldsValue({ ai_user_prompt: userPromptDefault })}
                className="custom-font text-xs p-0 h-auto"
              >
                پیش‌فرض
              </Button>
            </div>
          )}
          name="ai_user_prompt"
          extra={(
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              <span>متغیرها (برای افزودن کلیک کنید):</span>
              {promptVariables.map((variable) => (
                <Tag
                  key={variable}
                  color="purple"
                  className="cursor-pointer m-0 font-mono"
                  dir="ltr"
                  onClick={() => appendVariable(variable)}
                >
                  {variable}
                </Tag>
              ))}
              <span>— اگر {'{transcript}'} در قالب نباشد، متن مکالمه به انتهای آن اضافه می‌شود.</span>
            </div>
          )}
          rules={[{ max: PROMPT_MAX_LENGTH, message: 'حداکثر ۲۰٬۰۰۰ کاراکتر' }]}
        >
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} placeholder={userPromptDefault} showCount maxLength={PROMPT_MAX_LENGTH} />
        </Form.Item>
      </SectionCard>
    </div>
  );
};

export default AiSummarySettingsTab;
